import { motion } from 'framer-motion';
import { ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';

interface OnboardingScreenProps {
  videoSrc?: string;
  posterSrc?: string;
  heroContent?: ReactNode;
  headline: string;
  subtext: string;
  icon?: LucideIcon;
  children?: ReactNode;
}

export const OnboardingScreen = ({
  videoSrc,
  posterSrc,
  heroContent,
  headline,
  subtext,
  icon: Icon,
  children,
}: OnboardingScreenProps) => {
  return (
    <div className="flex flex-col h-full w-full">
      {/* Hero Section - Top 2/3 */}
      <div className="relative flex-[2] overflow-hidden bg-muted">
        {heroContent ? (
          heroContent
        ) : videoSrc ? (
          <video
            src={videoSrc}
            poster={posterSrc}
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
        ) : posterSrc ? (
          <motion.img
            src={posterSrc}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ scale: 1 }}
            animate={{ scale: 1.05 }}
            transition={{
              duration: 10,
              repeat: Infinity,
              repeatType: 'reverse',
              ease: 'linear',
            }}
          />
        ) : (
          <div className="absolute inset-0 shimmer" />
        )}
        
        {/* Bottom fade – always present so visuals recede behind the text panel */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background to-transparent pointer-events-none" />
      </div>

      {/* Content Section - Bottom 1/3 */}
      <div className="flex-1 bg-background px-8 pb-8 pb-safe flex flex-col justify-between">
        <div className="text-center space-y-3 mb-6">
          {Icon && (
            <div className="flex justify-center mb-2">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Icon className="w-6 h-6 text-primary" />
              </div>
            </div>
          )}
          <h1 className="text-[1.75rem] font-bold text-heading tracking-tight leading-[1.2] text-pretty">
            {headline}
          </h1>
          <p className="text-body text-base leading-relaxed max-w-xs mx-auto">
            {subtext}
          </p>
        </div>

        <div className="space-y-3">
          {children}
        </div>
      </div>
    </div>
  );
};
